import axios from "axios";

// Base API host from environment
const API_HOST = process.env.REACT_APP_API_HOST;
// Root host (without /api) — Sanctum's CSRF endpoint lives here
const APP_HOST = (process.env.REACT_APP_HOST || API_HOST.replace(/\/api\/?$/, '/')).replace(/\/?$/, '/');

// Session is carried by an httpOnly cookie (Sanctum stateful SPA mode).
// Global defaults so that plain `axios` calls (login/register) also send credentials.
axios.defaults.baseURL = API_HOST;
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const axiosInstance = axios.create({
  baseURL: API_HOST,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: "application/json",
  },
});

/**
 * Ensures the XSRF-TOKEN cookie exists before a state-changing auth request
 * (login / register / autologin). Idempotent and cheap.
 */
export const ensureCsrfCookie = () => {
  purgeDomainScopedXsrfCookies();
  return axios.get('sanctum/csrf-cookie', { baseURL: APP_HOST });
};

/**
 * Browsers keep a host-only cookie and a Domain-scoped cookie with the same name side by side
 * and send the older one first, so a stale Domain-scoped XSRF-TOKEN (issued when the backend used
 * SESSION_DOMAIN) would be picked over the fresh host-only one and every POST would fail with 419.
 * XSRF-TOKEN is not httpOnly, so it can be expired from here for the host and its parent domains.
 */
function purgeDomainScopedXsrfCookies() {
  if (typeof document === 'undefined') return;
  const parts = window.location.hostname.split('.');
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    document.cookie = `XSRF-TOKEN=; Max-Age=0; path=/; domain=${domain}${secure}`;
  }
}

// Rutas públicas donde NO debe redirigirse a /login ante un 401
const PUBLIC_PATHS = ['/login', '/register', '/recover-password', '/home', '/help', '/'];
const PUBLIC_PATH_PREFIXES = ['/register/', '/reset-password/'];

function isPublicPath(path) {
  if (!path) return false;
  if (PUBLIC_PATHS.includes(path)) return true;
  return PUBLIC_PATH_PREFIXES.some((p) => path.startsWith(p));
}

// Centralized response error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    // 419 = CSRF token mismatch / expired session cookie. Refresh the cookie and retry once.
    if (status === 419 && error.config && !error.config._csrfRetried) {
      error.config._csrfRetried = true;
      try {
        await ensureCsrfCookie();
        return axiosInstance.request(error.config);
      } catch (e) { /* fall through */ }
    }

    if (status === 401) {
      // Session is gone: redirect to login, but only when on a protected route.
      const loginPath = '/login';
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        if (currentPath !== loginPath && !isPublicPath(currentPath)) {
          window.location.href = loginPath;
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
